"use client";

import { useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRouter } from "next/navigation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, Loader2 } from "lucide-react";
import { useFetchQuery } from "@/hooks/useFetchQuery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import { cn } from "@/lib/utils";
import {
  BILLING_CYCLES,
  SUBSCRIPTION_PLAN_KEYS,
} from "@/config/enums/subscription.enum";
import { useTranslation } from "@/hooks/useLanguage";

// Saudi B2B VAT rate. Hardcoded because this public card is shown pre-subscription
// (the dynamic accounting rate isn't reachable here). The actual ZATCA invoice at
// signup uses the dynamic rate; this is an informational "what you'll pay" figure.
const VAT_RATE_PERCENT = 15;
const VAT_RATE = VAT_RATE_PERCENT / 100;

/**
 * Pre-VAT plan.price → VAT-inclusive amount, formatted as a 2-decimal string.
 * Half-away-from-zero on cents matches pms-api's ZATCA rounding rule.
 */
function formatVatInclusive(basePrice) {
  if (basePrice == null || !Number.isFinite(Number(basePrice))) return "—";
  const gross = Number(basePrice) * (1 + VAT_RATE);
  const cents =
    gross >= 0 ? Math.floor(gross * 100 + 0.5) : Math.ceil(gross * 100 - 0.5);
  return (cents / 100).toFixed(2);
}

export const Pricing = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.YEARLY);

  const { data: plansResponse, isLoading } = useFetchQuery(
    "getActiveSubscriptionPlans",
  );

  const plans = useMemo(() => {
    return plansResponse?.data?.plans || plansResponse?.data?.result || [];
  }, [plansResponse]);

  const filteredPlans = useMemo(() => {
    return plans
      .filter((plan) => plan.billingCycle === billingCycle)
      .sort((a, b) => (a.price || 0) - (b.price || 0));
  }, [plans, billingCycle]);
  const featureLabels = {
    booking: "subscriptionPlans.form.feature_booking",
    channelManager: "subscriptionPlans.form.feature_channel_manager",
    dynamicPricing: "subscriptionPlans.form.feature_dynamic_pricing",
    housekeeping: "subscriptionPlans.form.feature_housekeeping",
    accounting: "subscriptionPlans.form.feature_accounting",
    zatcaEInvoice: "subscriptionPlans.form.feature_zatca",
    whatsapp: "subscriptionPlans.form.feature_whatsapp",
    sms: "subscriptionPlans.form.feature_sms",
    aiReviews: "subscriptionPlans.form.feature_ai_reviews",
    advancedReports: "subscriptionPlans.form.feature_advanced_reports",
  };

  const handleGetStarted = (plan) => {
    if (!isAuthenticated) {
      router.push("/register#registration-form");
      return;
    }
    // If authenticated, do nothing for now as per requirement
    console.log(`User is authenticated, selected plan: ${plan.name}`);
  };

  const renderPlanCard = (plan) => (
    <Card
      key={plan._id}
      className={cn(
        "h-full flex flex-col border-border bg-card/5 backdrop-blur-sm transition-all duration-300 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10",
        plan.key === SUBSCRIPTION_PLAN_KEYS.PRO
          ? "border-primary/50 shadow-lg shadow-primary/5 ring-1 ring-primary/20"
          : "",
      )}
    >
      <CardHeader className="space-y-3 pb-3 pt-5">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-foreground uppercase tracking-tight">
              {plan.name}
            </CardTitle>
            {plan.key === "pro" && (
              <Badge className="bg-primary text-white border-0 hover:bg-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">
                {t("pricing.most_popular")}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-end gap-1.5">
          <span className="text-4xl font-extrabold tracking-tighter text-foreground leading-none">
            {plan.price}
          </span>
          <div className="flex flex-col mb-1 leading-none">
            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
              {t("common.currency_sar")}
            </span>
            <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-tighter">
              /
              {plan.billingCycle === BILLING_CYCLES.YEARLY
                ? t("pricing.year")
                : t("common.month")}
            </span>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground -mt-1 leading-none">
          {t("pricing.price_incl_vat")
            .replace("{amount}", formatVatInclusive(plan.price))
            .replace("{currency}", plan.currency || t("common.currency_sar"))
            .replace("{rate}", String(VAT_RATE_PERCENT))}
        </p>

        <CardDescription className="line-clamp-2 text-[12px] text-muted-foreground leading-relaxed">
          {plan.description ||
            `${t("pricing.perfect_for")} ${plan.name.toLowerCase()} ${t(
              "organization.detail.properties",
            )}.`}
        </CardDescription>
      </CardHeader>

      <CardContent className="pb-5">
        <Button
          className="w-full font-bold py-5 transition-all duration-300 rounded-lg text-xs uppercase tracking-widest bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/30 border-0"
          onClick={() => handleGetStarted(plan)}
        >
          {t("pricing.get_started")}
        </Button>
      </CardContent>

      <div className="px-6">
        <hr className="border-border/50" />
      </div>

      <CardFooter className="flex-grow flex-col items-start pt-5 pb-7 px-6">
        <ul className="space-y-3 w-full text-[12px]">
          <li className="flex items-start gap-3">
            <div className="mt-1 bg-primary/20 rounded-full p-0.5">
              <Check className="h-2.5 w-2.5 text-primary shrink-0" />
            </div>
            <span className="text-foreground font-medium">
              {t("pricing.up_to")} {plan.limits?.maxRoomsPerProperty}{" "}
              {t("common.rooms")}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <div className="mt-1 bg-primary/20 rounded-full p-0.5">
              <Check className="h-2.5 w-2.5 text-primary shrink-0" />
            </div>
            <span className="text-foreground font-medium">
              {t("pricing.up_to")} {plan.limits?.maxUsersPerProperty}{" "}
              {t("pricing.users")}
            </span>
          </li>

          {/* Usage Limits */}
          {plan.usageLimits?.smsPerMonth && (
            <li className="flex items-start gap-3">
              <div className="mt-1 bg-primary/20 rounded-full p-0.5">
                <Check className="h-2.5 w-2.5 text-primary shrink-0" />
              </div>
              <span className="text-foreground font-medium">
                {plan.usageLimits.smsPerMonth} {t("pricing.sms_per_month")}
              </span>
            </li>
          )}
          {plan.usageLimits?.whatsappPerMonth && (
            <li className="flex items-start gap-3">
              <div className="mt-1 bg-primary/20 rounded-full p-0.5">
                <Check className="h-2.5 w-2.5 text-primary shrink-0" />
              </div>
              <span className="text-foreground font-medium">
                {plan.usageLimits.whatsappPerMonth}{" "}
                {t("pricing.whatsapp_per_month")}
              </span>
            </li>
          )}
          {plan.usageLimits?.invoicesPerMonth && (
            <li className="flex items-start gap-3">
              <div className="mt-1 bg-primary/20 rounded-full p-0.5">
                <Check className="h-2.5 w-2.5 text-primary shrink-0" />
              </div>
              <span className="text-foreground font-medium">
                {plan.usageLimits.invoicesPerMonth}{" "}
                {t("pricing.invoices_per_month")}
              </span>
            </li>
          )}

          {/* All Features */}
          {Object.entries(plan.features || {})
            .filter(([_, enabled]) => enabled)
            .map(([key, _]) => (
              <li key={key} className="flex items-start gap-3">
                <div className="mt-1 bg-primary/20 rounded-full p-0.5">
                  <Check className="h-2.5 w-2.5 text-primary shrink-0" />
                </div>
                <span className="capitalize text-foreground font-medium">
                  {featureLabels[key]
                    ? t(featureLabels[key])
                    : key.replace(/([A-Z])/g, " $1").trim()}
                </span>
              </li>
            ))}
        </ul>
      </CardFooter>
    </Card>
  );

  return (
    <section id="pricing" className="container py-24 sm:py-32 overflow-hidden">
      <div className="text-center space-y-4 mb-12">
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
          {t("pricing.title_1")}{" "}
          <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 text-transparent bg-clip-text">
            {t("common.pricing")}
          </span>
        </h2>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          {t("pricing.description")}
        </p>
      </div>

      <div className="flex flex-col items-center gap-12">
        <Tabs
          defaultValue={BILLING_CYCLES.YEARLY}
          className="w-full max-w-[400px] mb-8"
          onValueChange={setBillingCycle}
        >
          <TabsList
            variant="green"
            className="bg-muted border border-border p-1.5 h-14"
          >
            <TabsTrigger
              variant="green"
              value={BILLING_CYCLES.YEARLY}
              className="rounded-md h-full "
            >
              {t("common.yearly")}
            </TabsTrigger>
            <TabsTrigger
              variant="green"
              value={BILLING_CYCLES.MONTHLY}
              className="rounded-md h-full"
            >
              {t("common.monthly")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            {t("pricing.no_plans_available")}
          </div>
        ) : (
          <Carousel
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full max-w-6xl mx-auto px-4 mt-4"
          >
            <CarouselContent>
              {filteredPlans.map((plan) => (
                <CarouselItem
                  key={plan._id}
                  className="md:basis-1/2 lg:basis-1/3 p-4"
                >
                  {renderPlanCard(plan)}
                </CarouselItem>
              ))}
            </CarouselContent>
            {filteredPlans.length > 3 && (
              <>
                <CarouselPrevious className="-left-4 lg:-left-12 border-border bg-background/20 backdrop-blur-md hover:bg-background/40" />
                <CarouselNext className="-right-4 lg:-right-12 border-border bg-background/20 backdrop-blur-md hover:bg-background/40" />
              </>
            )}
          </Carousel>
        )}
      </div>
    </section>
  );
};
